import { useEffect, useState } from 'react';

const STUDIO_SPOTS_URL = 'https://mockfast.io/backend/apitemplate/get/46NX13RYMI';

type BackendSpot = {
  id?: string;
  y?: number;
  x?: number;
  type?: string;
  bookable?: boolean;
};

type BackendResponse = {
  id?: string;
  name?: string;
  spots?: BackendSpot[];
};

export type StudioSpot = {
  id: string;
  y: number;
  x: number;
  type: string;
  bookable: boolean;
};

export type StudioSpotsData = {
  id: string;
  name: string;
  spots: StudioSpot[];
};

const mapSpot = (spot: BackendSpot): StudioSpot => ({
  id: typeof spot.id === 'string' ? spot.id : 'unknown',
  y: typeof spot.y === 'number' ? spot.y : 0,
  x: typeof spot.x === 'number' ? spot.x : 0,
  type: typeof spot.type === 'string' ? spot.type : 'unknown',
  bookable: Boolean(spot.bookable)
});

const mapResponse = (response: BackendResponse): StudioSpotsData => ({
  id: typeof response.id === 'string' ? response.id : 'unknown',
  name: typeof response.name === 'string' ? response.name : 'Unnamed studio',
  spots: Array.isArray(response.spots) ? response.spots.map(mapSpot) : []
});

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return 'name' in error && (error as { name?: string }).name === 'AbortError';
};

export function useStudioSpotsCard() {
  const [data, setData] = useState<StudioSpotsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setIsError(false);

      try {
        const response = await fetch(STUDIO_SPOTS_URL, {
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${String(response.status)}`);
        }

        const raw = (await response.json()) as BackendResponse;
        const mapped = mapResponse(raw);

        if (isMounted) {
          setData(mapped);
        }
      } catch (error) {
        if (isMounted && !isAbortError(error)) {
          setIsError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  return {
    isError,
    isLoading,
    data
  };
}
